import { observer } from 'mobx-react-lite';
import React from 'react';
import styled, { ThemeProps } from 'styled-components';
import ClipLoader from 'react-spinners/ClipLoader';
import { Card } from 'styleguide/Card';
import { colors } from 'styleguide/colors';
import { DividerLine } from 'styleguide/DividerLine';
import { Modal } from 'styleguide/Modal';
import { H5, P3 } from 'styleguide/Texts';
import { Icon, MicroIcon } from 'styleguide/Icons';
import { View } from 'styleguide/View';
import { CropImage } from '../CropImage/CropImage';
import { Input } from 'styleguide/Inputs';
import { Select, Multiselect, isUpdate } from 'components/Multiselect';
import { ExerciseImage } from 'components/ExerciseImage';
import { CustomExerciseViewModel } from './CustomExerciseViewModel';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { Theme } from 'styleguide/types';
import toast from 'react-hot-toast';
import { PrimaryButton, SecondaryButton } from 'styleguide/Buttons';
import { FlexRow } from 'styleguide/Row';
import fonts from 'styleguide/fonts';
import { t } from 'translations/t';

const Container = styled(View)`
  width: 554px;
  max-width: 554px;
  @media (max-width: 1024px) {
    width: 80vw;
  }
`;

const Label = styled(P3)`
  font-weight: ${fonts.medium.weight};
`;

const Top = styled(FlexRow)`
  justify-content: space-between;
  padding: ${Spacing.lg}px;
`;

const CameraCircle = styled(Card)`
  height: 104px;
  width: 104px;
  border-radius: 52px;
  margin-bottom: 16px;
  align-items: center;
  justify-content: center;
  background-color: ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'dark' ? colors.greyUltraDark : colors.greyHiperLight};
  cursor: pointer;
`;

const TitleInput = styled(Input)`
  border-radius: ${CornerRadius.md}px;
  border: 1px solid ${(props: ThemeProps<Theme>) => props.theme.border};
  background-color: transparent;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  font-size: 14px;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${Spacing.md}px ${Spacing.lg}px;
  gap: ${Spacing.md}px;
`;

const CellContainer = styled(View)`
  gap: ${Spacing.xs}px;
`;

const CropContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 80vh;
`;

const DismissButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  cursor: pointer;
  padding: ${Spacing.xs}px;
`;

export interface CustomExerciseModalProps {
  vm: CustomExerciseViewModel;
  isOpen: boolean;
  onClose: () => void;
  onExerciseSaved: ((exerciseTemplateId: string) => void) | undefined;
}

export const CustomExerciseModal = observer(
  ({ vm, isOpen, onClose, onExerciseSaved }: CustomExerciseModalProps) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleSelectImage = () => {
      fileInputRef.current?.click();
    };

    return (
      <Modal isOpen={isOpen} onClose={onClose} contentStyle={{ padding: 0 }}>
        <Container>
          {vm.imageUrlToCrop ? (
            <CropContainer>
              <CropImage
                imageUrl={vm.imageUrlToCrop}
                onImageCropped={vm.onUploadImage}
                onCancel={vm.onCancelImageCrop}
              />
            </CropContainer>
          ) : (
            <>
              <Top>
                <H5>{vm.modalTitle}</H5>
                <DismissButtonContainer onClick={onClose}>
                  <MicroIcon type="cross" />
                </DismissButtonContainer>
              </Top>
              <DividerLine />
              <View style={{ alignItems: 'center', padding: '16px 0' }}>
                <input
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  type="file"
                  accept="image/png, image/jpeg"
                  onChange={e => {
                    const file = e?.target.files?.[0];
                    if (file) {
                      vm.onFileSystemImageSelected(file);
                    }
                  }}
                />
                <CameraCircle onClick={handleSelectImage}>
                  <ImageComponent
                    isLoadingImage={vm.isUploadingImage}
                    remoteThumbnailUrl={vm.remoteThumbnailUrl}
                  />
                </CameraCircle>
                <SecondaryButton
                  title={t('web.customExerciseModal.addImg')}
                  onClick={handleSelectImage}
                />
              </View>
              <InnerContainer>
                <CellContainer>
                  <Label>{t('web.customExerciseModal.exName')}</Label>
                  <TitleInput
                    placeholder={t('web.customExerciseModal.exName.placeholder')}
                    value={vm.title}
                    onChange={e => vm.onTitleUpdate(e.currentTarget.value)}
                  />
                </CellContainer>
                {!vm.isEditingExistingExercise && (
                  <CellContainer>
                    <Label>{t('createExercise.cell.exerciseType.title')}</Label>

                    <Select
                      value={vm.selectedExerciseType}
                      onChange={e => {
                        if (isUpdate(e) || e === undefined) {
                          vm.onExerciseTypeUpdate(e);
                        }
                      }}
                      options={vm.exerciseTypeOptions}
                    />
                  </CellContainer>
                )}
                <CellContainer>
                  <Label>{t('createExercise.cell.equipment.title')}</Label>
                  <Select
                    options={vm.equipmentOptions}
                    value={vm.selectedEquipment}
                    onChange={e => {
                      if (isUpdate(e) || e === undefined) {
                        vm.onEquipmentUpdate(e);
                      }
                    }}
                  />
                </CellContainer>
                <CellContainer>
                  <Label>{t('createExercise.cell.primaryMuscleGroup.title')}</Label>
                  <Select
                    options={vm.muslceGroupOptions}
                    value={vm.selectedMuslceGroup}
                    menuPlacement={'top'}
                    onChange={e => {
                      if (isUpdate(e) || e === undefined) {
                        vm.onMuscleGroupUpdate(e);
                      }
                    }}
                  />
                </CellContainer>
                <CellContainer>
                  <Label>{t('createExercise.cell.secondaryMuscleGroups.title')}</Label>
                  <Multiselect
                    options={vm.secondaryMuscleGroupOptions}
                    value={vm.selectedSecondaryMuscleGroups}
                    menuPlacement={'top'}
                    onChange={(e: any) => vm.onSecondaryMuscleGroupUpdate(e)}
                  />
                </CellContainer>
              </InnerContainer>
              <DividerLine />
              <InnerContainer>
                <View style={{ alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                  <PrimaryButton
                    title={vm.saveButtonTitle}
                    onClick={async () => {
                      try {
                        const createdExerciseId = await vm.onSave();
                        if (createdExerciseId) {
                          onExerciseSaved?.(createdExerciseId);
                        }
                      } catch {
                        toast.error(t('web.customExerciseModal.saveError'));
                      } finally {
                        onClose();
                      }
                    }}
                    enabled={vm.isSaveEnabled}
                    loading={vm.isSaving}
                    style={{ minWidth: 120 }}
                  />
                </View>
              </InnerContainer>
            </>
          )}
        </Container>
      </Modal>
    );
  },
);

interface ImageComponentProp {
  isLoadingImage: boolean;
  remoteThumbnailUrl?: string;
}

const ImageComponent = ({ isLoadingImage, remoteThumbnailUrl }: ImageComponentProp) => {
  if (isLoadingImage) {
    return <ClipLoader size={20} color={colors.primary} loading={true} />;
  }

  if (!!remoteThumbnailUrl) {
    return <ExerciseImage source={remoteThumbnailUrl} diameter={80} />;
  }

  return <Icon type="image" tint={colors.greyMedium} />;
};
